import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { CREDIT_ERROR_FR } from "@/lib/creditErrors";

const MEMBER_WORDING =
  "Les crédits mensuels sont utilisés en premier. Les crédits achetés restent disponibles jusqu’à leur utilisation.";

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={testid}>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}

// ---- Organization member: read-only balance -----------------------------
export function OrgCreditBalance({ orgId }) {
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!orgId) return;
    let live = true;
    // Reset immediately on org switch so a stale balance is never shown, and a
    // late response for a previous org can never overwrite the current one.
    setC(null);
    setErr(false);
    api.get(`/organizations/${orgId}/credits`)
      .then((r) => { if (live) setC(r.data); })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [orgId]);
  // Fail-safe: never keep/show an old balance on error.
  if (err) return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700"
      data-testid="org-credits-error">
      Solde de crédits indisponible
    </div>
  );
  if (!c) return <div className="text-sm text-slate-400" data-testid="org-credits-loading">Chargement des crédits…</div>;
  return (
    <div data-testid="org-credit-balance">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Crédits mensuels" value={c.monthly_balance} testid="cb-monthly" />
        <Stat label="Crédits achetés" value={c.purchased_balance} testid="cb-purchased" />
        <Stat label="Total disponible" value={c.available} testid="cb-total" />
      </div>
      <p className="mt-2 text-xs text-slate-500" data-testid="cb-wording">{MEMBER_WORDING}</p>
    </div>
  );
}

// ---- Action price + "deducted once" notice (member, pre-execution) -------
// `onAvailability(bool)` reports whether the paid AI action can actually run, so the
// parent can hard-disable submission. Availability is derived ONLY from the backend
// response (price present, active subscription, non-demo org). Fail-closed on error.
export function ActionCreditPrice({ orgId, actionType, label, onAvailability }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!orgId) return;
    let live = true;
    setInfo(null);
    api.get(`/organizations/${orgId}/credits`)
      .then((r) => { if (live) setInfo(r.data); })
      .catch(() => { if (live) setInfo(false); });
    return () => { live = false; };
  }, [orgId]);

  const price = info && info !== false ? info.action_prices?.[actionType] : null;
  const available =
    info && info !== false &&
    price != null &&
    info.subscription_active === true &&
    info.is_demo !== true;

  useEffect(() => {
    onAvailability?.(!!available);
  }, [available, onAvailability]);

  if (info === null) return null; // loading (undetermined)
  if (!available) {
    return <span className="text-xs font-medium text-amber-600" data-testid="action-price-unavailable">Action IA indisponible</span>;
  }
  return (
    <span className="text-xs text-slate-600" data-testid="action-price">
      {label} : <strong>{price} crédits</strong> · solde {info.available ?? 0} · déduit une seule fois
    </span>
  );
}

// ---- Platform Admin: balances + ledger + audited adjustment --------------
export function AdminOrgCredits({ orgs }) {
  const [orgId, setOrgId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ bucket: "monthly", amount: "", reason: "" });
  const [idemKey, setIdemKey] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [msg, setMsg] = useState(null);

  const reqRef = React.useRef(0);
  const load = useCallback((id) => {
    if (!id) return;
    const token = ++reqRef.current; // ignore late responses from a previous org
    setLoading(true);
    setData(null);
    api.get(`/admin/organizations/${id}/credits`)
      .then((r) => { if (reqRef.current === token) setData(r.data); })
      .catch(() => { if (reqRef.current === token) setData(false); })
      .finally(() => { if (reqRef.current === token) setLoading(false); });
  }, []);
  useEffect(() => { if (orgId) load(orgId); }, [orgId, load]);

  const amountNum = parseInt(form.amount, 10);
  const validAmount = Number.isInteger(amountNum) && amountNum !== 0;
  const curBucket = form.bucket === "monthly" ? (data?.wallet?.monthly_balance ?? 0) : (data?.wallet?.purchased_balance ?? 0);
  const wouldGoNegative = validAmount && curBucket + amountNum < 0;

  const beginConfirm = () => {
    setMsg(null);
    if (!validAmount || !form.reason.trim() || wouldGoNegative) return;
    if (!idemKey) setIdemKey(crypto.randomUUID()); // stable key; reused on retry
    setConfirming(true);
  };

  const submit = async () => {
    if (pending) return; // prevent double submission
    setPending(true);
    setMsg(null);
    try {
      const r = await api.post(`/admin/organizations/${orgId}/credits/adjust`, {
        bucket: form.bucket, amount: amountNum, reason: form.reason.trim(), idempotency_key: idemKey,
      });
      const a = r.data.adjustment;
      setMsg({ ok: true, text: `Ajustement appliqué · mensuel ${a.monthly_balance} · acheté ${a.purchased_balance}` });
      setForm({ bucket: "monthly", amount: "", reason: "" });
      setIdemKey(null); // new key only for a genuinely new adjustment
      setConfirming(false);
      load(orgId);
    } catch (err) {
      const code = err?.response?.data?.error_code;
      setMsg({ ok: false, text: (code && CREDIT_ERROR_FR[code]) || "Échec de l’ajustement." });
      // keep idemKey so a retry of THIS adjustment reuses the same key
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mt-8" data-testid="admin-org-credits">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-500">Lodge Credits</h3>
      <div className="mb-4 max-w-sm">
        <label className="mb-1 block text-xs text-slate-500">Organisation</label>
        <select className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          data-testid="credits-org-select" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
          <option value="">Sélectionner…</option>
          {(orgs || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>

      {loading && <div className="text-sm text-slate-400">Chargement…</div>}
      {data && data !== false && (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="admin-credits-panel">
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Plan" value={data.plan || "—"} testid="ac-plan" />
            <Stat label="Statut" value={data.subscription_status || "—"} testid="ac-status" />
            <Stat label="Mensuel" value={data.wallet.monthly_balance} testid="ac-monthly" />
            <Stat label="Acheté" value={data.wallet.purchased_balance} testid="ac-purchased" />
          </div>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Total" value={data.wallet.available} testid="ac-total" />
            <Stat label="Période" value={data.wallet.current_period_key || "—"} testid="ac-period" />
          </div>
          {!data.subscription_active && (
            <p className="mb-3 text-xs font-medium text-amber-600" data-testid="ac-paid-ai-unavailable">
              IA payante indisponible pour cette organisation.
            </p>
          )}

          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Écritures récentes</div>
          {(!data.ledger || data.ledger.length === 0) ? (
            <div className="text-sm text-slate-400" data-testid="ac-ledger-empty">Aucune écriture.</div>
          ) : (
            <div className="max-h-48 overflow-y-auto rounded border border-slate-100" data-testid="ac-ledger">
              {data.ledger.map((e) => (
                <div key={e.id} className="flex justify-between gap-2 border-b border-slate-50 px-3 py-1.5 text-xs">
                  <span className="text-slate-600">{e.kind}{e.action_type ? ` · ${e.action_type}` : ""}</span>
                  <span className="font-mono text-slate-800">{e.amount} (m{e.monthly_after}/a{e.purchased_after})</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 border-t border-slate-100 pt-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Ajustement manuel (audité)</div>
            <div className="grid gap-2 sm:grid-cols-4">
              <select className="rounded-md border border-slate-300 px-2 py-2 text-sm" data-testid="adj-bucket"
                value={form.bucket} onChange={(e) => setForm((f) => ({ ...f, bucket: e.target.value }))}>
                <option value="monthly">Mensuel</option>
                <option value="purchased">Acheté</option>
              </select>
              <input type="number" placeholder="Montant (+/-)" className="rounded-md border border-slate-300 px-2 py-2 text-sm"
                data-testid="adj-amount" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
              <input type="text" placeholder="Motif (obligatoire)" className="rounded-md border border-slate-300 px-2 py-2 text-sm sm:col-span-2"
                data-testid="adj-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
            </div>
            {validAmount && (
              <p className="mt-1 text-xs text-slate-500" data-testid="adj-direction">
                {amountNum > 0 ? "Ajout" : "Retrait"} de {Math.abs(amountNum)} crédits ({form.bucket === "monthly" ? "mensuel" : "acheté"}).
              </p>
            )}
            {wouldGoNegative && <p className="mt-1 text-xs font-medium text-red-600" data-testid="adj-negative">Solde négatif interdit.</p>}
            {!confirming ? (
              <button className="mt-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
                data-testid="adj-begin" disabled={!validAmount || !form.reason.trim() || wouldGoNegative} onClick={beginConfirm}>
                Ajuster les crédits
              </button>
            ) : (
              <div className="mt-2 flex items-center gap-2" data-testid="adj-confirm">
                <span className="text-sm text-slate-700">Confirmer {amountNum > 0 ? "+" : ""}{amountNum} ?</span>
                <button className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
                  data-testid="adj-submit" disabled={pending} onClick={submit}>{pending ? "…" : "Confirmer"}</button>
                <button className="rounded-md border border-slate-300 px-3 py-1.5 text-sm" data-testid="adj-cancel"
                  disabled={pending} onClick={() => setConfirming(false)}>Annuler</button>
              </div>
            )}
            {msg && <p className={`mt-2 text-xs ${msg.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="adj-msg">{msg.text}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
